import * as React from "react"
import { createPortal } from "react-dom"
import "./contact.css"

/** "Get in touch" (Figma 51:126): a message and an e-mail address, sent from the
 *  page. On desktop the menu's button leaves the menu and becomes a bar in the
 *  middle of the screen (ContactOverlay); on phones the open menu's own panel
 *  takes the form (ContactForm, "card"). States: idle, typing, error, sent. */

// where it's sent: the site's own function (functions/api/contact.ts), which mails it on
const SEND_URL = "/api/contact"
// sent: how long its fields take to go (contact.css, ct-out: the last of six, 5 x 55ms + 360ms)
const LEAVE_MS = 660
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** the monospace's width per character (12px JetBrains Mono: 0.6em) */
let charW = 7.2
function measureChar() {
    const c = document.createElement("canvas").getContext("2d")
    if (!c) return
    c.font = '500 12px "NTO Mono", monospace'
    const w = c.measureText("0000000000").width / 10
    if (w > 4) charW = w
}

async function send(message: string, email: string, website: string, t: number) {
    // (t: how long the form's been open, against bots: functions/api/contact.ts)
    const r = await fetch(SEND_URL, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ message, email, website, t }) })
    if (!r.ok) throw new Error(String(r.status))
}

function Dots({ sent }: { sent?: boolean }) {
    // the send button's three dots; sent: five, a cross
    const at = sent
        ? [
              [0, 0],
              [16, 0],
              [8, 8],
              [0, 16],
              [16, 16],
          ]
        : [
              [0, 0],
              [0, 15.6],
              [9.1, 7.8],
          ]
    return (
        <span className={"ct-dots" + (sent ? " ct-dots-sent" : "")} aria-hidden="true">
            {at.map(([l, t], k) => (
                <i key={k} style={{ left: l, top: t }} />
            ))}
        </span>
    )
}

type Variant = "bar" | "card"
export type FormState = "idle" | "sent"

/** The form itself: its fields, their lines (eaten by the text as it's typed),
 *  checking and sending. */
export function ContactForm({ variant, stacked, onSent, onClose, autoFocus }: { variant: Variant; stacked?: boolean; onSent?: () => void; onClose?: () => void; autoFocus?: boolean }) {
    const [message, setMessage] = React.useState("")
    const [email, setEmail] = React.useState("")
    const [errors, setErrors] = React.useState<{ message?: boolean; email?: boolean }>({})
    const [state, setState] = React.useState<"idle" | "sending" | "sent">("idle")
    const [failed, setFailed] = React.useState(false)
    const [leaving, setLeaving] = React.useState(false)
    const trap = React.useRef<HTMLInputElement>(null)
    const opened = React.useRef(Date.now())
    const msgRef = React.useRef<HTMLInputElement & HTMLTextAreaElement>(null)
    const field = React.useRef<HTMLDivElement>(null)
    const [cw, setCw] = React.useState(charW)
    const [room, setRoom] = React.useState(80)

    // how many characters fit: the field's width (the line keeps a little of it)
    React.useLayoutEffect(() => {
        const fit = () => {
            measureChar()
            setCw(charW)
            const w = field.current?.clientWidth || 600
            setRoom(variant === "bar" ? Math.max(10, Math.floor((w - 40) / charW)) : Math.floor(w / charW))
        }
        fit()
        ;(document as any).fonts?.ready?.then(fit)
        // (its room changes as the bar grows into place, and with the window)
        const ro = new ResizeObserver(fit)
        if (field.current) ro.observe(field.current)
        return () => ro.disconnect()
    }, [variant])
    React.useEffect(() => {
        if (autoFocus) setTimeout(() => msgRef.current?.focus({ preventScroll: true }), 700)
    }, [])

    const rows = variant === "card" ? 3 : 1
    const max = room * rows - (variant === "card" ? 4 : 0)
    const submit = (e?: React.FormEvent) => {
        e?.preventDefault()
        if (state !== "idle") return
        const err = { message: !message.trim(), email: !EMAIL.test(email.trim()) }
        setErrors(err)
        if (err.message || err.email) return
        setState("sending")
        setFailed(false)
        send(message.trim(), email.trim(), trap.current?.value || "", Date.now() - opened.current)
            .then(() => {
                // (its fields go first, a beat apart; then the thank-you)
                setLeaving(true)
                setTimeout(() => {
                    setState("sent")
                    onSent?.()
                }, LEAVE_MS)
            })
            // (not sent: said where the title is, the message kept, to try again)
            .catch(() => (setState("idle"), setFailed(true)))
    }

    // the message's lines: each row's line starts after that row's text
    const lens = React.useMemo(() => {
        if (rows === 1) return [message.length]
        const out: number[] = []
        for (let r = 0; r < rows; r++) out.push(Math.max(0, Math.min(room, message.length - r * room)))
        return out
    }, [message, room, rows])
    const placeholder = errors.message ? "You say nothing?" : "Your message"
    const shownLen = (r: number) => (message.length ? lens[r] : r === 0 ? placeholder.length : 0)

    if (state === "sent")
        return (
            <div className={`ct-form ct-${variant} ct-sent`} data-ct="sent">
                <span className="ct-label ct-in" style={{ ["--i" as any]: 1 }}>
                    Thank you, we’ll be in touch
                </span>
                {/* (the cross: closes it) */}
                <button type="button" className="ct-close ct-in" style={{ ["--i" as any]: 0 }} aria-label="Close" onClick={onClose}>
                    <Dots sent />
                </button>
            </div>
        )
    return (
        <form className={`ct-form ct-${variant}` + (stacked ? " ct-stack" : "")} onSubmit={submit} noValidate data-error={errors.message || errors.email || undefined} data-leaving={leaving || undefined}>
            <span className={"ct-label ct-in" + (failed ? " ct-failed" : "")} style={{ ["--i" as any]: 5 }} aria-live="polite">
                {failed ? "Not sent, try again?" : "Send me a message"}
            </span>
            {/* (left empty by people; bots fill it in) */}
            <input ref={trap} className="ct-trap" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div
                ref={field}
                className={"ct-msg ct-in" + (errors.message ? " ct-bad" : "")}
                style={{ ["--i" as any]: 4, ["--cw" as any]: cw + "px" }}
                // (a press anywhere on its line writes the message: the caret at its end)
                onMouseDown={(e) => {
                    const i = msgRef.current
                    if (!i || e.target === i) return
                    e.preventDefault()
                    i.focus({ preventScroll: true })
                    i.setSelectionRange(i.value.length, i.value.length)
                }}
            >
                {variant === "bar" ? (
                    <input
                        ref={msgRef}
                        className="ct-input"
                        value={message}
                        maxLength={max}
                        placeholder={placeholder}
                        aria-label="Your message"
                        onChange={(e) => (setMessage(e.target.value), errors.message && setErrors({ ...errors, message: false }))}
                        style={{ width: `calc(${Math.max(1, shownLen(0))} * var(--cw) + 2px)` }}
                    />
                ) : (
                    <textarea
                        ref={msgRef}
                        className="ct-input ct-area"
                        value={message}
                        maxLength={max}
                        rows={3}
                        placeholder={placeholder}
                        aria-label="Your message"
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
                        onChange={(e) => (setMessage(e.target.value.replace(/\n/g, " ")), errors.message && setErrors({ ...errors, message: false }))}
                        style={{ width: `calc(${room} * var(--cw))` }}
                    />
                )}
                {/* (the lines: after the text on each row) */}
                {Array.from({ length: rows }, (_, r) => (
                    <i key={r} className="ct-line" style={{ ["--row" as any]: r, ["--at" as any]: shownLen(r), ["--pad" as any]: shownLen(r) ? "10px" : "0px" }} />
                ))}
            </div>
            <span className="ct-label ct-in" style={{ ["--i" as any]: 2 }}>
                Your e-mail
            </span>
            <input
                className={"ct-input ct-email ct-in" + (errors.email ? " ct-bad" : "")}
                style={{ ["--i" as any]: 1 }}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                maxLength={80}
                placeholder="your e-mail"
                aria-label="Your e-mail"
                onChange={(e) => (setEmail(e.target.value), errors.email && setErrors({ ...errors, email: false }))}
            />
            <button className="ct-send ct-in" style={{ ["--i" as any]: 0 }} type="submit" aria-label="Send" disabled={state === "sending"}>
                <Dots />
            </button>
        </form>
    )
}

const EASE_OPEN = "cubic-bezier(.5,0,.12,1)" // in and out
const MS = 900
const STACK_BELOW = 1140 // (screen width) the e-mail under the message, below this
const STACK_H = 120
const STACK_R = 20 // (its corners: a rounded box, not a pill)
const radius = (h: number) => (h > 75 ? STACK_R : h / 2)
const isStacked = () => innerWidth < STACK_BELOW
const APPEAR_AT = 180 // the circle: just after the menu's begun to go
const CIRCLE_MS = 300 // scaling in
// (and opening as it's still growing: the circle's slowing as the opening picks
// up, so it grows on without a moment's stop)
const OPEN_AT = APPEAR_AT + 30

/** Desktop: "Get in touch" closes the menu (as ever) and the bar opens out of
 *  the middle of a blurred screen, with an ease in and out; the form comes in
 *  right to left. A click on the blur closes it. */
export function ContactOverlay() {
    const [from, setFrom] = React.useState<DOMRect | null>(null)
    const [closing, setClosing] = React.useState(false)
    const [sent, setSent] = React.useState(false)
    // (out of the open menu, the screen's already blurred: it stays so, no unblurring and blurring again)
    const [blurred, setBlurred] = React.useState(false)
    const box = React.useRef<HTMLDivElement>(null)
    const veil = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        const open = (e: Event) => {
            setSent(false)
            setClosing(false)
            setBlurred(!!document.querySelector('.nto27[data-menu="true"]'))
            setToMenu(false)
            setFrom((e as CustomEvent<DOMRect>).detail)
        }
        addEventListener("nto:contact", open)
        return () => removeEventListener("nto:contact", open)
    }, [])

    // (narrower screens: the e-mail goes under the message, the bar twice as high,
    // so the message keeps room to be written in)
    const [stacked, setStacked] = React.useState(false)
    const sentRef = React.useRef(false)
    sentRef.current = sent
    const target = (wide: boolean) => {
        const W = wide ? Math.min(1429, innerWidth - 80) : Math.min(541, innerWidth - 80)
        const H = wide && isStacked() ? STACK_H : 75
        return { left: (innerWidth - W) / 2, top: (innerHeight - H) / 2, width: W, height: H }
    }
    const place = (b: HTMLElement, r: ReturnType<typeof target>) =>
        Object.assign(b.style, { left: r.left + "px", top: r.top + "px", width: r.width + "px", height: r.height + "px", borderRadius: radius(r.height) + "px" })
    React.useLayoutEffect(() => {
        if (!from || !box.current) return
        setStacked(isStacked())
        const t = target(true)
        const b = box.current
        place(b, t)
        // (out of the middle of the screen: a dot of its height, opening out to its width)
        const cx = innerWidth / 2
        // (first a circle, scaling in out of nothing; then it opens, hard in and out)
        b.animate([{ scale: 0 }, { scale: 1 }], { duration: CIRCLE_MS, delay: APPEAR_AT, easing: "cubic-bezier(.33,.5,.5,1)", fill: "backwards" })
        b.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: APPEAR_AT, easing: "linear", fill: "backwards" })
        // (a circle at first, whatever its corners end up)
        b.animate(
            [
                { left: cx - t.height / 2 + "px", width: t.height + "px", borderRadius: t.height / 2 + "px" },
                { left: t.left + "px", width: t.width + "px", borderRadius: radius(t.height) + "px" },
            ],
            { duration: MS, delay: OPEN_AT, easing: EASE_OPEN, fill: "backwards" }
        )
        const resize = () => {
            if (!sentRef.current) setStacked(isStacked())
            place(b, target(!sentRef.current))
        }
        addEventListener("resize", resize)
        return () => removeEventListener("resize", resize)
    }, [from])

    // sent: the bar closes in to the thank-you (and goes after a while)
    React.useEffect(() => {
        if (!sent || !box.current) return
        const b = box.current
        const a = b.getBoundingClientRect(),
            t = target(false)
        b.animate(
            [
                { left: a.left + "px", top: a.top + "px", width: a.width + "px", height: a.height + "px", borderRadius: getComputedStyle(b).borderRadius },
                { left: t.left + "px", top: t.top + "px", width: t.width + "px", height: t.height + "px", borderRadius: radius(t.height) + "px" },
            ],
            { duration: 700, easing: "cubic-bezier(.76,0,.24,1)" }
        )
        place(b, t)
    }, [sent])

    const close = () => {
        if (closing) return
        // (at once, even while it's still opening: it stops where it is, and goes)
        const b = box.current
        if (b)
            for (const a of b.getAnimations()) {
                try {
                    a.commitStyles()
                } catch {}
                a.cancel()
            }
        setClosing(true)
        setTimeout(() => (setFrom(null), setClosing(false)), 450)
    }
    // (the menu button stays; pressed meanwhile, the bar goes and the menu opens
    // instead, over the same blur: it's held till the menu's own is there, at once)
    const [toMenu, setToMenu] = React.useState(false)
    React.useEffect(() => {
        if (!from) return
        const press = (e: MouseEvent) => {
            if (!(e.target as Element | null)?.closest?.(".sh .n27-navbtn")) return
            setToMenu(true)
            close()
        }
        addEventListener("click", press, true)
        return () => removeEventListener("click", press, true)
    }, [from, closing])
    React.useLayoutEffect(() => {
        if (from || !toMenu) return
        // (gone: the menu's blur, which it held off, comes on at once, no fading in)
        const html = document.documentElement
        html.setAttribute("data-veil-instant", "")
        setTimeout(() => html.removeAttribute("data-veil-instant"), 120)
        setToMenu(false)
    }, [from])
    // (the page under it is locked meanwhile: no scrolling, no page cursor)
    React.useEffect(() => {
        if (!from) return
        const html = document.documentElement
        html.setAttribute("data-contact", "")
        const overflow = html.style.overflow
        html.style.overflow = "hidden"
        return () => (html.removeAttribute("data-contact"), void (html.style.overflow = overflow))
    }, [from])
    React.useEffect(() => {
        if (!from) return
        const key = (e: KeyboardEvent) => e.key === "Escape" && close()
        addEventListener("keydown", key)
        return () => removeEventListener("keydown", key)
    }, [from, closing])

    if (!from) return null
    const state = { "data-closing": closing || undefined, "data-blurred": blurred || undefined, "data-to-menu": toMenu || undefined }
    return (
        <>
            {/* the blur: in the shell, under the menu (shell.css: menu z 4, this z 3), so
                the menu goes out over it, and the bar comes in over both */}
            <div className="ct-overlay ct-under" {...state}>
                <div ref={veil} className="ct-veil" onClick={close} />
            </div>
            {createPortal(
                <div className="ct-overlay ct-over" {...state}>
                    <div ref={box} className="ct-box" role="dialog" aria-label="Get in touch" data-sent={sent || undefined}>
                        <ContactForm variant="bar" stacked={stacked} autoFocus onSent={() => setSent(true)} onClose={close} />
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}

/** open it from the menu's button (desktop) */
export const openContact = (from: HTMLElement) => dispatchEvent(new CustomEvent("nto:contact", { detail: from.getBoundingClientRect() }))
